import { DragEndEvent } from '@dnd-kit/core';
import type { TFunction } from 'i18next';
import {
  BlockDraftWorkoutDomain,
  PublishedWorkoutStatusDomain,
  WorkoutLayerDomain,
} from '@strength/domain';
import {
  MetricBenchmarkData,
  Workout,
  mergeWorkoutLayersByWorkoutId,
} from '@strength/domain-workout';
import { tKey } from '@strength/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useMutation, useQuery, useQueryOne } from '../../../loco-sync';
import { trackEvent } from '../../../reporting/reporting';
import { trpc } from '../../../trpc/react';
import {
  DeleteConfirmationModal,
  deleteBlockWorkoutText,
} from '../../components/modals/delete-confirmation-modal';
import {
  applyMultiWorkoutTemplateActualsText,
  applyMultiWorkoutTemplateDraftOverwriteText,
  applyMultiWorkoutTemplateOverflowText,
} from './apply-multi-workout-template-text';
import { ConfirmationModal } from '../../components/modals/confirmation-modal';
import { Sheet, useSheetDndSensors } from '../../components/sheet';
import { useViewer } from '../../context/viewer';
import { useBenchmarks } from '../../hooks/useBenchmarks';
import { useMetrics } from '../../hooks/useMetrics';
import { useToast } from '../../hooks/useToast';
import { jotaiStore } from '../../utils/jotai';
import { WorkoutTemplateDrawer } from '../library/workout-templates/components/WorkoutTemplateDrawer';
import {
  WorkoutTemplateData,
  WorkoutTemplateSelection,
  getNewMultiWorkoutTemplate,
  getNewMultiWorkoutSectionTemplate,
  getNewWorkoutTemplate,
  removeInstructionsFromLayers,
} from '../library/workout-templates/workout-template-utils';
import { BlockAllView } from './block-all/BlockAllView';
import {
  getCycleForDate,
  getDateOrderedInsertPosition,
} from './block-all/calendar-utils';
import { SingleWorkoutEditContext } from './block-all/SingleWorkoutSheet';
import type {
  ApplySwapData,
  TrackExerciseSwapInfo,
} from './components/apply-swap-drawer';
import {
  ApplySwapDrawer,
  addRecentSwap,
  findMatchingExercisesInFutureWorkouts,
  generateSwapCommandsForFutureInstances,
  recentSwapsAtom,
  removeRecentSwap,
} from './components/apply-swap-drawer';
import { getContextMenuContent } from './context-menu/context-menu';
import { WithWorkoutId } from './context-menu/data-for-context-menu';
import {
  collectMultiDayPasteCommandsByPosition,
  pasteCommands,
} from './copy-paste/copy-paste';
import { groupSelectionData } from './copy-paste/group-data';
import {
  buildWorkoutClipboard,
  workoutClipboardAtom,
} from './copy-paste/workout-clipboard';
import { handleDragEnd } from './dnd';
import { getGeneratedAssignedDate } from './getGeneratedAssignedDate';
import { calculateSheetDimensions, getGridTemplateColumns } from './grid-utils';
import { useBlockExerciseVideos } from './hooks/useBlockExerciseVideos';
import { InstructionDrawer } from './instruction-drawer';
import { MiscMutationFns, WorkoutLayerCommand } from './mutation-fns/types';
import {
  commandsForBulkClear,
  findWorkoutLayer,
  makeWorkoutLayerMutationFns,
} from './mutation-fns/workout-draft';
import { CreateSchemePayload, NewSchemeDialog } from './new-scheme-dialog';
import { isPublishActionDisabled } from './publish/publish-state';
import {
  deletePublishTasks,
  usePublishErrorsForWorkouts,
} from './publish/utils';
import { createSchemeInput, selectSchemeCommands } from './scheme';
import { collapsedExercisesAtom, collapsedSectionsAtom } from './slots/common';
import { getSlotsForBlock } from './slots/workout';
import {
  AddSessionSide,
  BaseBlockSheetProps,
  BlockGroupBy,
  BlockSelectionData,
  InstructionEditData,
  SetGroupedData,
  WorkoutWithCycleAndSession,
} from './types';
import { useBlockDraftWorkoutUndoRedo } from './undo-redo/block';
import {
  getDisplayNameForWorkout,
  mergeWorkoutsWithCyclesAndSessions,
  parseEmptySlotCellId,
} from './utils';
import {
  resolveCreateSlot,
  runBlockMutations as runMutations,
} from './mutation-fns/run-mutations';
import { getWorkoutStatusesForBase } from './workout-statuses';
import {
  MultiWorkoutTemplateApplyPlan,
  selectMultiWorkoutTemplateCommands,
  selectWorkoutTemplateCommands,
} from './workout-template';

export function BlockSheet({
  block,
  hasBlockBeenPublished,
  publishState,
  blockWorkoutLayers,
  actualWorkoutLayers,
  blockPublishedWorkoutLayers,
  athletePublishedWorkoutLayers,
  modifiedPublishedWorkoutLayers,
  view,
  focusOptions,
  workoutTemplateTagOptions,
  onBlockLayoutChange,
  groupBy,
  singleAthleteProfileId,
  selectedWorkoutId,
  readOnly,
}: {
  onBlockLayoutChange: (cycleSortOrder: number, sessions: number) => void;
  groupBy?: BlockGroupBy;
  // Set only when the block has a single athlete (SC-3344).
  singleAthleteProfileId?: string | null;
  selectedWorkoutId?: string;
} & BaseBlockSheetProps) {
  // SC-5486: distance-constrained activation so the sheet's click-drag cell
  // selection and dnd-kit's drag share the same pointer gesture without
  // fighting (plain clicks select, a move past 8px drags).
  const sheetSensors = useSheetDndSensors();
  const { t } = useTranslation();
  const autoScheduleBlock = block.autoSchedule;
  const {
    navbar: { navbarTopColor, navbarBottomColor },
    team: { selectedTeam },
    org: {
      settings: { enableTeamLibrary },
    },
    hasPermissions,
  } = useViewer();
  const canCreateScheme = hasPermissions('library.schemes', 'create');
  const canCreateTemplate = hasPermissions('library.templates', 'create');

  const brandColors = { navbarBottomColor, navbarTopColor };
  const { defaultBenchmarkGridId } = useBenchmarks();

  // Fetch the single athlete's benchmarks so the base sheet converts `%` to
  // weight. Disabled (and skipped) with multiple athletes (SC-3344).
  const effectiveTeamId = selectedTeam || block.teamId;
  const { data: singleAthleteExerciseBenchmarks } =
    trpc.athleteBenchmarks.fetchAllExerciseBenchmarksForAthlete.useQuery(
      { profileId: singleAthleteProfileId ?? '' },
      { enabled: !!singleAthleteProfileId, staleTime: 10_000 },
    );
  const { data: maxConversions } =
    trpc.exerciseMaxConversionLibrary.allExerciseMaxConversions.useQuery(
      undefined,
      {
        enabled:
          !!singleAthleteProfileId && (!enableTeamLibrary || !!effectiveTeamId),
        staleTime: 10_000,
      },
    );
  // Per-athlete KPI targets resolve only for the single-athlete base sheet, the
  // same condition under which intensity `%` resolves (SC-3838). With multiple
  // athletes the map stays empty, so KPI `%` is left literal like intensity.
  const { data: kpiBenchmarks } =
    trpc.athleteKpiBenchmarks.fetchKpiBenchmarksForAthlete.useQuery(
      { profileId: singleAthleteProfileId ?? '' },
      {
        enabled: !!singleAthleteProfileId,
        staleTime: 10_000,
      },
    );
  // Empty arrays (multiple athletes) mean no match, so `%` is left as-is.
  const applyBenchmarkData: MetricBenchmarkData = {
    currentExerciseBenchmarks: singleAthleteExerciseBenchmarks ?? [],
    maxConversions: maxConversions ?? [],
    trainingMaxRatio: block.trainingMaxRatio,
    defaultBenchmarkGridId,
    kpiBenchmarks: kpiBenchmarks ?? {},
  };

  // Write-only subscription (SC-5564): the sheet no longer re-renders when a
  // copy updates the clipboard; paste reads the current value via jotaiStore.
  const setWorkoutClipboard = useSetAtom(workoutClipboardAtom);
  // Keep the atom mounted for the sheet's lifetime (SC-5564 review): the
  // subscription drives storage hydration and cross-tab sync without the
  // component re-rendering on value changes.
  useEffect(() => jotaiStore.sub(workoutClipboardAtom, () => {}), []);
  const [instructionEditData, setInstructionEditData] =
    useState<null | InstructionEditData>(null);
  const [workoutTemplateData, setWorkoutTemplateData] =
    useState<null | WorkoutTemplateData>(null);
  const [newSchemeData, setNewSchemeData] = useState<
    WithWorkoutId<SetGroupedData>[] | null
  >(null);
  const [modalOrDrawerOpen, setModalOrDrawerOpen] = useState(false);
  const [applySwapData, setApplySwapData] = useState<ApplySwapData | null>(
    null,
  );
  // A multi-workout template apply gated behind overflow / draft-overwrite
  // warnings: the generated plan is held here until all warnings are accepted.
  const [pendingMultiApply, setPendingMultiApply] = useState<{
    plan: MultiWorkoutTemplateApplyPlan;
    template: WorkoutTemplateData;
    // warning currently shown: null = none, proceed straight to commit
    warning: 'overflow' | 'draftOverwrite' | 'actuals' | null;
  } | null>(null);
  const workoutStatuses = getWorkoutStatusesForBase(
    blockWorkoutLayers,
    blockPublishedWorkoutLayers,
    athletePublishedWorkoutLayers,
    actualWorkoutLayers,
    !!singleAthleteProfileId,
  );
  // Tombstoned layers (deletedAt set) must stay in this query — the grid
  // layout builder uses them to hide block-layer workouts that were deleted
  // for an athlete in single-athlete mode.
  const { data: athleteDraftWorkoutLayers } = useQuery(
    'AthleteDraftWorkoutLayer',
    {
      blockId: block.id,
    },
  );

  const [sessionToDelete, setSessionToDelete] = useState<{
    cycleSortOrder: number;
    sessionSortOrder: number;
  } | null>(null);

  // In-memory new draft workout for an All-view "add workout" in progress.
  // Holds the new workout (pre-filled date, mapped cycle, computed sort order)
  // and the ids of later siblings to bump. Nothing here is persisted: the
  // draft layer + bump are committed on the first real edit (see
  // `commitNewDraftWorkout`), and the draft is dropped if the panel closes
  // with no edit.
  const [newDraftWorkout, setNewDraftWorkout] = useState<{
    workout: WorkoutWithCycleAndSession;
    bumpedWorkoutIds: string[];
  } | null>(null);

  const isPublishDisabled = isPublishActionDisabled(publishState);
  const [workoutToCancel, setWorkoutToCancel] = useState<string | null>(null);
  const [workoutToDelete, setWorkoutToDelete] = useState<string | null>(null);
  const { getMetric, getMetrics, setsMetric } = useMetrics();
  const metrics = useMemo(() => {
    return getMetrics(true);
  }, [getMetrics]);

  // Canceled workouts are read-only and were already published, so render them
  // from the current published layer (which has its sets expanded) instead of
  // the draft layer (which still carries the unexpanded Sets metric).
  const blockLayersForDisplay = blockWorkoutLayers.map((layer) => {
    const isCanceled =
      workoutStatuses.find((s) => s.workoutId === layer.workoutId)?.status
        .type === 'canceled';
    if (!isCanceled) return layer;
    const published = blockPublishedWorkoutLayers.find(
      (p) => p.workoutId === layer.workoutId && p.isCurrent,
    );
    return published ? { ...layer, layer: published.layer } : layer;
  });

  const workouts = mergeWorkoutsWithCyclesAndSessions(
    block,
    blockWorkoutLayers,
    mergeWorkoutLayersByWorkoutId(blockLayersForDisplay),
    actualWorkoutLayers,
    modifiedPublishedWorkoutLayers,
    workoutStatuses,
  );
  useBlockExerciseVideos(workouts);

  // Workouts visible in the current session/cycle view
  const displayWorkouts = workouts.filter((w) =>
    view.mode === 'session'
      ? w.session.id === view.sessionId
      : w.cycle.id === view.cycleId,
  );

  const { undo, redo, bulkMutate } = useBlockDraftWorkoutUndoRedo(block.id);
  const mutation = useMutation();

  // The workouts the All view (and its create path) sees: the real workouts
  // plus any in-progress add stub. The stub must be visible here so the normal
  // create path (`runMutations`) can read its mapped cycle, sort order, and
  // pre-filled date when the first edit persists it.
  const allViewWorkouts = newDraftWorkout
    ? [...workouts, newDraftWorkout.workout]
    : workouts;

  // If `workoutId` is the pending new draft workout, commit it on its first
  // real edit: bump later siblings and update the cycle's session count. The
  // draft layer itself is created by the normal mutation path, which reads the
  // new workout from `allViewWorkouts`. No-op for any other workout id.
  function commitNewDraftWorkout(workoutId: string) {
    const draft = newDraftWorkout;
    if (!draft || draft.workout.workoutId !== workoutId) {
      return;
    }
    const bumpWorkoutSessions = draft.bumpedWorkoutIds.flatMap((id) => {
      const layer = blockWorkoutLayers.find((l) => l.workoutId === id);
      if (!layer) return [];
      return [
        {
          module: BlockDraftWorkoutDomain.Module,
          identity: { workoutId: layer.workoutId, blockId: block.id },
          edits: [
            {
              modelName: 'BlockDraftWorkoutLayer' as const,
              modelId: layer.id,
              action: 'update' as const,
              data: { sessionSortOrder: layer.sessionSortOrder + 1 },
            },
          ],
        },
      ];
    });
    if (bumpWorkoutSessions.length > 0) {
      bulkMutate(bumpWorkoutSessions);
    }
    const cycleId = draft.workout.cycle.id;
    const newSessionCount =
      workouts.filter((w) => w.cycle.id === cycleId).length + 1;
    onBlockLayoutChange(cycleId, newSessionCount);
    setNewDraftWorkout(null);
  }

  const { data: publishTask } = useQueryOne('BlockPublishTask', {
    blockId: block.id,
  });
  const workoutPublishErrors = usePublishErrorsForWorkouts(
    workouts,
    publishTask,
    (workout) => {
      if (!workout.assignedDate) {
        return new Map([['assignedDate', [tKey('assignedDateIsRequired')]]]);
      }
      return undefined;
    },
  );

  const { toast } = useToast();

  const rawMutation = useMutation();
  const mutationFns = makeWorkoutLayerMutationFns(
    'BlockDraftWorkoutLayer',
    blockWorkoutLayers,
    (command) => runCommands([command]),
    setsMetric,
    undefined,
  );

  // I think these scheme functions are the same as the athlete ones, other than the bulkMutate fn

  const createScheme = trpc.schemeLibrary.createScheme.useMutation();
  const onCreateScheme = (schemePayload: CreateSchemePayload) => {
    const result = createSchemeInput(
      block,
      workouts,
      focusOptions,
      schemePayload,
      getMetric,
    );
    if (result.ok) {
      createScheme.mutate(result.value, {
        onSuccess: () =>
          toast({
            title: t('scheme.created', { schemeName: result.value.name }),
            severity: 'success',
          }),
        onError: () =>
          toast({
            title: t('somethingWentWrong'),
            severity: 'error',
          }),
      });
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const onSelectScheme: MiscMutationFns['onSelectScheme'] = (
    section,
    exercise,
    scheme,
  ) => {
    const result = selectSchemeCommands(
      block,
      workouts,
      blockWorkoutLayers,
      section,
      exercise,
      scheme,
      'BlockDraftWorkoutLayer',
      setsMetric,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  function recomputeDraftWithDate(
    prev: { workout: WorkoutWithCycleAndSession; bumpedWorkoutIds: string[] },
    assignedDate: string | null,
  ) {
    if (!assignedDate)
      return { ...prev, workout: { ...prev.workout, assignedDate } };
    const cycle = getCycleForDate(assignedDate, block.sortedCycles);
    if (!cycle) return { ...prev, workout: { ...prev.workout, assignedDate } };
    const cycleSessions = workouts
      .filter(
        (w) =>
          w.workoutId !== prev.workout.workoutId && w.cycle.id === cycle.id,
      )
      .map((w) => ({
        workoutId: w.workoutId,
        sessionSortOrder: w.session.id,
        assignedDate: getAssignedDate(w) ?? w.assignedDate,
      }));
    const { sessionSortOrder, bumpedWorkoutIds } = getDateOrderedInsertPosition(
      cycleSessions,
      assignedDate,
    );
    return {
      ...prev,
      bumpedWorkoutIds,
      workout: {
        ...prev.workout,
        assignedDate,
        cycle,
        session: {
          id: sessionSortOrder,
          name: `${block.sessionLabel} ${sessionSortOrder}`,
        },
      },
    };
  }

  // TODO: Is this actually what we want here?
  const onEditWorkoutDate: MiscMutationFns['onEditWorkoutDate'] = (
    workoutId,
    assignedDate,
  ) => {
    // Changing the pre-filled date on a not-yet-saved draft is NOT a real edit
    // — update it locally so a later content edit persists the right slot.
    if (newDraftWorkout && newDraftWorkout.workout.workoutId === workoutId) {
      setNewDraftWorkout((prev) =>
        prev ? recomputeDraftWithDate(prev, assignedDate) : prev,
      );
      return;
    }

    const commands: BlockDraftWorkoutDomain.Command[] = [];

    // Handle the workout being edited
    const edits: BlockDraftWorkoutDomain.Edit[] = [];
    // SC-5512: resolution is by workout id only — an empty-slot cell's key
    // is not a workout id, so it resolves to nothing and the create branch
    // below builds a fresh row at the key's slot.
    const { workoutLayer, identity } = findWorkoutLayer(
      blockWorkoutLayers,
      workoutId,
      'BlockDraftWorkoutLayer',
    );

    const match = workouts.find((w) => w.workoutId === workoutId);

    let modelId: string;
    if (workoutLayer) {
      modelId = workoutLayer.id;
    } else {
      modelId = v4();
      // SC-5512: the create carries its slot explicitly from the cell and a
      // freshly minted identity — never the (possibly empty-slot) lookup key
      // and never a silent slot-0 fallback.
      const { cycleSortOrder, sessionSortOrder } = resolveCreateSlot(
        workoutId,
        workouts,
      );
      edits.push({
        modelName: 'BlockDraftWorkoutLayer',
        modelId: modelId,
        action: 'create',
        data: {
          cycleSortOrder,
          sessionSortOrder,
          layer: WorkoutLayerDomain.defaultLayer(),
          name: null,
        },
      });
    }

    edits.push({
      modelName: 'BlockDraftWorkoutLayer',
      modelId,
      action: 'update',
      data: { assignedDate },
    });

    commands.push({
      module: BlockDraftWorkoutDomain.Module,
      identity: {
        // SC-5512: a slot-bound row keeps its real id; a create mints a
        // fresh identity (the lookup key may be an empty-slot cell key).
        workoutId: workoutLayer ? identity.workoutId : v4(),
        blockId: block.id,
      },
      edits,
    });

    // If editing a session in cycle 1, update all matching sessions in other cycles
    if (autoScheduleBlock && match && match.cycle.id === 1) {
      const sessionId = match.session.id;
      const otherCycleWorkouts = workouts.filter(
        (w) => w.session.id === sessionId && w.cycle.id !== 1,
      );

      // Create a reference workout with the updated date for getGeneratedAssignedDate
      const firstMatchingSession = {
        ...match,
        assignedDate,
      };

      for (const otherWorkout of otherCycleWorkouts) {
        const numberOfDaysForCycle = block.layout[otherWorkout.cycle.id];
        if (!numberOfDaysForCycle) {
          continue;
        }

        const generatedDate = autoScheduleBlock
          ? getGeneratedAssignedDate(
              block.startDate,
              otherWorkout,
              numberOfDaysForCycle,
              firstMatchingSession,
            )
          : null;

        const { workoutLayer: otherWorkoutLayer } = findWorkoutLayer(
          blockWorkoutLayers,
          otherWorkout.workoutId,
          'BlockDraftWorkoutLayer',
        );
        if (!otherWorkoutLayer || generatedDate === null) {
          // if the other workout layer doesn't exist
          // or the generated date is null, don't update the workout
          continue;
        }

        commands.push({
          module: BlockDraftWorkoutDomain.Module,
          identity: {
            workoutId: otherWorkout.workoutId,
            blockId: block.id,
          },
          edits: [
            {
              modelName: 'BlockDraftWorkoutLayer',
              modelId: otherWorkoutLayer.id,
              action: 'update',
              data: { assignedDate: generatedDate },
            },
          ],
        });
      }
    }

    bulkMutate(commands);
  };

  const onEditWorkoutName: MiscMutationFns['onEditWorkoutName'] = (
    workoutId,
    name,
  ) => {
    // SC-5512: slot-aware resolution — empty-slot cells have no workout id.
    // The returned identity carries the bound row's real id, or a minted
    // model id with exists=false (the create path mints the identity).
    const { identity } = findWorkoutLayer(
      blockWorkoutLayers,
      workoutId,
      'BlockDraftWorkoutLayer',
    );
    const workoutLayerId = identity.id;
    const modelName = 'BlockDraftWorkoutLayer';
    runCommands([
      {
        identity,
        edits: [
          {
            modelName,
            modelId: workoutLayerId,
            action: 'update',
            data: {
              name: name ?? null,
            },
          },
        ],
      },
    ]);
  };

  const createUpdateSessionSortOrderCommand = (
    workout: (typeof blockWorkoutLayers)[number],
    sessionSortOrder: number,
  ): BlockDraftWorkoutDomain.Command => {
    return {
      module: BlockDraftWorkoutDomain.Module,
      identity: {
        workoutId: workout.workoutId,
        blockId: block.id,
      },
      edits: [
        {
          modelName: 'BlockDraftWorkoutLayer',
          modelId: workout.id,
          action: 'update',
          data: {
            sessionSortOrder,
          },
        },
      ],
    };
  };

  function updateSortOrderAndMutate(workouts: WorkoutWithCycleAndSession[]) {
    const commands: BlockDraftWorkoutDomain.Command[] = [];
    workouts
      .sort((a, b) => a.session.id - b.session.id)
      .forEach((w, index) => {
        // reset the session sort order to be 1-x
        // SC-5512: live rows join by their immutable workoutId — the cell
        // positions have already been bumped here, so a slot join would
        // bind the wrong row. Empty-slot cells carry a (cycle, session) key
        // instead of a workout id and can never match a layer.
        const blockWorkoutLayer = blockWorkoutLayers.find(
          (l) => l.workoutId === w.workoutId,
        );

        if (blockWorkoutLayer) {
          commands.push(
            createUpdateSessionSortOrderCommand(blockWorkoutLayer, index + 1),
          );
        }
      });
    return commands;
  }

  const onAddSession: MiscMutationFns['onAddSession'] = (
    cycleSortOrder: number,
    sessionSortOrder: number,
    side: AddSessionSide,
  ) => {
    const newWorkoutSortOrder =
      sessionSortOrder + (side === 'add-right' ? 1 : 0);

    // update all existing workouts in the cycle by bumping their session sort order if it's greater than or equal to the newly added workout
    const updatedWorkouts = workouts
      .filter((w) => w.cycle.id === cycleSortOrder)
      .map((w) => {
        if (w.session.id >= newWorkoutSortOrder) {
          w.session.id = w.session.id += 1;
        }
        return w;
      });

    // stub a new workout and insert it
    // note: this will get replaced when the page re-renders when the mutation completes
    // and the page re-renders, so the values aren't important
    updatedWorkouts.push({
      cycle: { id: cycleSortOrder, name: '', startDate: '', endDate: '' },
      session: {
        id: newWorkoutSortOrder,
        name: '',
      },
      workoutId: '',
      sections: [],
      exists: false,
      actual: undefined,
      status: { type: 'draft' },
      assignedDate: null,
      modifiedPublished: undefined,
      isAthleteOnlyLayer: false,
      name: null,
    });

    const commands = updateSortOrderAndMutate(updatedWorkouts);
    bulkMutate(commands);
    onBlockLayoutChange(cycleSortOrder, updatedWorkouts.length);
  };

  const doSessionDelete = ({
    cycleSortOrder,
    sessionSortOrder,
  }: {
    cycleSortOrder: number;
    sessionSortOrder: number;
  }) => {
    const updatedWorkouts = workouts.filter(
      (w) => w.cycle.id === cycleSortOrder && w.session.id !== sessionSortOrder,
    );

    const commands = updateSortOrderAndMutate(updatedWorkouts);

    const workoutLayer = blockWorkoutLayers.find(
      (e) =>
        e.cycleSortOrder === cycleSortOrder &&
        e.sessionSortOrder === sessionSortOrder,
    );
    if (workoutLayer) {
      // If we are deleting a workout from the db, do that first and make sure it
      // succeeds before updating the other layers' sort orders
      mutation.mutate(
        {
          module: BlockDraftWorkoutDomain.Module,
          identity: {
            workoutId: workoutLayer.workoutId,
            blockId: block.id,
          },
          edits: [
            {
              modelName: 'BlockDraftWorkoutLayer',
              modelId: workoutLayer.id,
              action: 'delete',
            },
          ],
        },
        {
          onSuccess: () => {
            bulkMutate(commands);
            onBlockLayoutChange(cycleSortOrder, updatedWorkouts.length);
          },
          onError: () => {
            toast({
              title: t('failedToDeleteWorkout'),
              severity: 'error',
            });
          },
        },
      );
    } else {
      // We are just removing a stub workout
      bulkMutate(commands);
      onBlockLayoutChange(cycleSortOrder, updatedWorkouts.length);
    }
  };

  const onRemoveSession: MiscMutationFns['onRemoveSession'] = (
    cycleSortOrder,
    sessionSortOrder,
  ) => {
    const workout = workouts.find(
      (w) => w.cycle.id === cycleSortOrder && w.session.id === sessionSortOrder,
    );
    if (workout?.exists) {
      // only prompt to delete if the workout has anything in it
      setSessionToDelete({ cycleSortOrder, sessionSortOrder });
    } else {
      doSessionDelete({ cycleSortOrder, sessionSortOrder });
    }
  };

  const doWorkoutCancel = (workoutId: string) => {
    // if publish is in progress don't allow cancel
    if (isPublishDisabled) {
      toast({
        title: t('publishInProgress'),
        severity: 'error',
      });
      return;
    }

    // delete any related publish tasks before canceling
    if (publishState.type === 'stalled') {
      deletePublishTasks(publishState.task, rawMutation.mutate);
    }

    const command: PublishedWorkoutStatusDomain.Command = {
      module: PublishedWorkoutStatusDomain.Module,
      type: 'CancelBlockWorkout',
      input: {
        blockId: block.id,
        workoutId,
      },
    };

    rawMutation.mutate(command);

    trackEvent('Strength | Block Builder | Cancel Base Workout', {
      blockId: block.id,
    });
    toast({
      title: t('success'),
      description: t('workoutCanceled'),
      severity: 'success',
    });
  };

  const onCancelWorkout: MiscMutationFns['onCancelWorkout'] = (
    workoutId: string,
  ) => {
    setWorkoutToCancel(workoutId);
  };

  const onDeleteWorkout: MiscMutationFns['onDeleteWorkout'] = (
    workoutId: string,
  ) => {
    setWorkoutToDelete(workoutId);
  };

  const onSetWorkoutTemplate = (
    workoutId: string,
    selection: WorkoutTemplateSelection,
  ) => {
    // get name for the workout
    const workout = workouts.find((w) => w.workoutId === workoutId);
    if (!workout) {
      return;
    }
    const name = workout.name ?? getDisplayNameForWorkout(workout);

    const layers = blockWorkoutLayers
      .filter((l) => l.workoutId === workoutId)
      .map((l) => l.layer);
    if (layers.length === 0) {
      return;
    }
    const template = getNewWorkoutTemplate(name, selection, layers);
    setWorkoutTemplateData(template);
  };

  const onSetMultiWorkoutTemplate = (
    selection: string[] | { workoutId: string; sectionIds: string[] }[],
  ) => {
    // section-level selections come from highlighting sections across days;
    // string[] comes from whole-day selections
    const isSectionSelection = selection.some((s) => typeof s !== 'string');
    const perDay: { workoutId: string; sectionIds: string[] }[] =
      isSectionSelection
        ? (selection as { workoutId: string; sectionIds: string[] }[])
        : (selection as string[]).map((workoutId) => ({
            workoutId,
            sectionIds: [],
          }));

    if (perDay.length < 2) {
      // single-day selections route to the existing single-workout flow
      const [day] = perDay;
      if (day) {
        onSetWorkoutTemplate(
          day.workoutId,
          day.sectionIds.length === 0 ? 'all' : day.sectionIds,
        );
      }
      return;
    }
    const workoutIds = perDay.map(({ workoutId }) => workoutId);
    const selectedWorkouts = workoutIds
      .map((id) => workouts.find((w) => w.workoutId === id))
      .filter((w) => !!w);
    if (selectedWorkouts.length < 2) {
      return;
    }
    const first = selectedWorkouts[0];
    const name = first
      ? (first.name ?? getDisplayNameForWorkout(first))
      : t('defaultTemplateName');
    const layersByWorkout = new Map(
      workoutIds.map((id) => [
        id,
        blockWorkoutLayers
          .filter((l) => l.workoutId === id)
          .map((l) => l.layer),
      ]),
    );
    setWorkoutTemplateData(
      isSectionSelection
        ? getNewMultiWorkoutSectionTemplate(
            name,
            selection as { workoutId: string; sectionIds: string[] }[],
            layersByWorkout,
          )
        : getNewMultiWorkoutTemplate(name, workoutIds, layersByWorkout),
    );
  };

  const createWorkoutTemplate =
    trpc.workoutTemplateLibrary.createWorkoutTemplate.useMutation();
  const onSaveWorkoutTemplate = (
    workoutTemplate: WorkoutTemplateData,
    saveInstructions: boolean,
  ) => {
    const { name, tags, template, id, isMultiDay } = workoutTemplate;
    let processedLayers = template;
    if (!saveInstructions) {
      processedLayers = removeInstructionsFromLayers(template);
    }
    const tagSelections = tags.map((tag, i) => ({
      id: v4(),
      sortOrder: i,
      tag,
    }));
    createWorkoutTemplate.mutate(
      {
        id,
        name,
        tags: tagSelections,
        template: processedLayers,
        isMultiDay,
      },
      {
        onSuccess: () =>
          toast({
            title: t('workoutTemplateAdded', { name }),
            severity: 'success',
          }),
        onError: () =>
          toast({
            title: t('somethingWentWrong'),
            severity: 'error',
          }),
      },
    );
  };

  const onSelectWorkoutTemplate: MiscMutationFns['onSelectWorkoutTemplate'] = (
    workoutToPasteInto: Workout,
    workoutTemplate: WorkoutTemplateData,
  ) => {
    // Multi-day apply maps days onto consecutive slots of the *visible* view,
    // following the same order the sheet renders. The All view has no
    // cycle/session columns to traverse, so it keeps the single-day flow.
    // Templates loaded from the library don't carry isMultiDay (the column
    // isn't persisted), so infer it from the day count.
    const isMultiDayTemplate =
      workoutTemplate.isMultiDay ?? workoutTemplate.template.length > 1;
    if (isMultiDayTemplate && (groupBy === 'session' || groupBy === 'cycle')) {
      // visible workouts only — traversal (and overflow) stops at the view
      // boundary, matching the multi-day paste helpers
      const orderedWorkouts = [...displayWorkouts].sort((a, b) =>
        groupBy === 'session'
          ? a.cycle.startDate.localeCompare(b.cycle.startDate)
          : a.session.id - b.session.id,
      );
      const result = selectMultiWorkoutTemplateCommands({
        orderedWorkouts,
        startWorkoutId: workoutToPasteInto.workoutId,
        template: workoutTemplate,
        blockWorkoutLayers,
        modelName: 'BlockDraftWorkoutLayer',
        setsMetric,
      });
      if (!result.ok) {
        toast({ title: t('pasteOperationError'), severity: 'error' });
        return;
      }
      const { overflow, draftWorkoutIds, actualsDayNumbers, commands } =
        result.value;
      // actuals days block the whole apply — nothing commits while any target
      // day has logged Actuals
      if (actualsDayNumbers.length > 0) {
        setPendingMultiApply({
          plan: result.value,
          template: workoutTemplate,
          warning: 'actuals',
        });
        return;
      }
      if (!overflow && draftWorkoutIds.length === 0) {
        // no warnings — commit straight away
        runCommands(commands);
        return;
      }
      setPendingMultiApply({
        plan: result.value,
        template: workoutTemplate,
        warning: overflow ? 'overflow' : 'draftOverwrite',
      });
      return;
    }

    const result = selectWorkoutTemplateCommands(
      workoutToPasteInto,
      blockWorkoutLayers,
      workoutTemplate,
      'BlockDraftWorkoutLayer',
      setsMetric,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const confirmPendingMultiApply = () => {
    if (!pendingMultiApply) return;
    const { plan } = pendingMultiApply;
    // the actuals warning blocks the apply entirely — confirming just closes
    if (pendingMultiApply.warning === 'actuals') {
      setPendingMultiApply(null);
      return;
    }
    // advance to the next warning or commit
    if (
      pendingMultiApply.warning === 'overflow' &&
      plan.draftWorkoutIds.length > 0
    ) {
      setPendingMultiApply({ ...pendingMultiApply, warning: 'draftOverwrite' });
      return;
    }
    setPendingMultiApply(null);
    runCommands(plan.commands);
  };

  const getAssignedDate = useCallback(
    (workout: WorkoutWithCycleAndSession) => {
      // If the workout already has an explicit draft layer, respect whatever
      // date it stores — including null. An auto-scheduled fallback would
      // override the user's explicit clear.
      // SC-5512: empty-slot cells carry a (cycle, session) key, not a
      // workout id — resolve those by the cell's slot. LIVE cells resolve by
      // their exact workoutId: a slot lookup could misbind if two live rows
      // ever shared a slot.
      const explicitLayer = parseEmptySlotCellId(workout.workoutId)
        ? blockWorkoutLayers.find(
            (l) =>
              l.cycleSortOrder === workout.cycle.id &&
              l.sessionSortOrder === workout.session.id,
          )
        : blockWorkoutLayers.find((l) => l.workoutId === workout.workoutId);
      if (explicitLayer) {
        return explicitLayer.assignedDate ?? null;
      }

      const firstMatchingSession = workouts.find(
        (w) => w.session.id === workout.session.id && w.cycle.id === 1,
      );
      const numberOfDaysForCycle = block.layout[workout.cycle.id];

      if (!numberOfDaysForCycle) {
        // if the cycle is not in the layout, don't set an assigned date
        return null;
      }

      // if the block is auto scheduled, use the generated assigned date,
      // otherwise don't set an assigned date
      return autoScheduleBlock
        ? getGeneratedAssignedDate(
            block.startDate,
            workout,
            numberOfDaysForCycle,
            firstMatchingSession,
          )
        : null;
    },
    [
      blockWorkoutLayers,
      workouts,
      block.startDate,
      block.layout,
      autoScheduleBlock,
    ],
  );

  // While a new draft workout is pending, report its pre-filled date so the
  // create persists the clicked day (the draft has no layer to read from yet).
  const getAssignedDateWithStub = useCallback(
    (workout: WorkoutWithCycleAndSession) =>
      newDraftWorkout && workout.workoutId === newDraftWorkout.workout.workoutId
        ? newDraftWorkout.workout.assignedDate
        : getAssignedDate(workout),
    [newDraftWorkout, getAssignedDate],
  );

  const runCommands = (commands: WorkoutLayerCommand[]) => {
    // A create targeting the pending new draft is its first real edit — commit
    // it (bump siblings + update layout) so the workout lands at its slot.
    for (const command of commands) {
      if (!command.identity.exists) {
        commitNewDraftWorkout(command.identity.workoutId);
      }
    }
    runMutations(
      commands,
      block.id,
      allViewWorkouts,
      bulkMutate,
      getAssignedDateWithStub,
    );
  };

  const createNewDraftWorkout = (
    date: string,
  ): WorkoutWithCycleAndSession | null => {
    const cycle = getCycleForDate(date, block.sortedCycles);
    if (!cycle) {
      return null;
    }
    const cycleSessions = workouts
      .filter((w) => w.cycle.id === cycle.id)
      .map((w) => ({
        workoutId: w.workoutId,
        sessionSortOrder: w.session.id,
        assignedDate: getAssignedDate(w) ?? w.assignedDate,
      }));
    const { sessionSortOrder, bumpedWorkoutIds } = getDateOrderedInsertPosition(
      cycleSessions,
      date,
    );
    const workout: WorkoutWithCycleAndSession = {
      workoutId: v4(),
      sections: [],
      exists: false,
      actual: undefined,
      modifiedPublished: undefined,
      assignedDate: date,
      status: { type: 'draft' },
      session: {
        id: sessionSortOrder,
        name: `${block.sessionLabel} ${sessionSortOrder}`,
      },
      cycle,
      isAthleteOnlyLayer: false,
      name: null,
    };
    setNewDraftWorkout({ workout, bumpedWorkoutIds });
    return workout;
  };

  const cancelNewDraftWorkout = () => setNewDraftWorkout(null);

  const handleTrackExerciseSwap = (info: TrackExerciseSwapInfo) => {
    const hasFutureInstances =
      findMatchingExercisesInFutureWorkouts(
        info.workoutId,
        info.oldLibraryExerciseId,
        workouts,
      ).length > 0;

    if (!hasFutureInstances) return;

    const swapData: ApplySwapData = {
      ...info,
      isBlockLayer: true,
      hasFutureInstances,
      hasMatchingAthletes: false,
    };

    jotaiStore.set(
      recentSwapsAtom,
      addRecentSwap(jotaiStore.get(recentSwapsAtom), swapData),
    );
  };

  const handleApplySwapSave = (options: {
    applyToFutureInstances: boolean;
    applyToOtherAthletes: boolean;
    selectedAthleteIds: string[];
  }) => {
    if (!applySwapData) return;

    const commands: WorkoutLayerCommand[] = [];

    if (options.applyToFutureInstances) {
      const matchingExercises = findMatchingExercisesInFutureWorkouts(
        applySwapData.workoutId,
        applySwapData.oldLibraryExerciseId,
        workouts,
      );
      const swapCommands = generateSwapCommandsForFutureInstances(
        matchingExercises,
        applySwapData.newLibraryExerciseId,
        applySwapData.newExerciseName,
        'BlockDraftWorkoutLayer',
        blockWorkoutLayers,
      );
      commands.push(...swapCommands);
    }

    if (commands.length > 0) {
      runCommands(commands);
      setWorkoutClipboard(null);
      toast({
        title: t('success'),
        description: t('exerciseChangeAppliedToFuture'),
        severity: 'success',
      });
    }

    jotaiStore.set(
      recentSwapsAtom,
      removeRecentSwap(
        jotaiStore.get(recentSwapsAtom),
        applySwapData.exerciseId,
      ),
    );
    setApplySwapData(null);
  };

  const miscMutationFns: MiscMutationFns = {
    onSelectScheme,
    onEditWorkoutDate,
    onEditWorkoutName,
    onAddSession,
    onRemoveSession,
    onCancelWorkout,
    onDeleteWorkout,
    onSelectWorkoutTemplate,
  };

  const collapsedSections = useAtomValue(collapsedSectionsAtom);
  const collapsedExercises = useAtomValue(collapsedExercisesAtom);
  const {
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    maxMetricsPerExercise,
  } = useMemo(() => {
    return calculateSheetDimensions(displayWorkouts, null, block.status, {
      collapsedSections,
      collapsedExercises,
    });
  }, [displayWorkouts, collapsedSections, block.status, collapsedExercises]);

  const sheetProps = getSlotsForBlock({
    workouts,
    allErrors: workoutPublishErrors,
    view,
    mutationFns,
    miscMutationFns,
    applyBenchmarkData,
    setModalOrDrawerOpen,
    hasBlockBeenPublished,
    isPublishDisabled,
    blockStatus: block.status,
    allowFlexWorkouts: block.allowFlexWorkouts,
    getMetric,
    selectedWorkoutId,
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    getAssignedDate,
    applySwapCallbacks: {
      onOpenApplySwap: setApplySwapData,
      onTrackExerciseSwap: handleTrackExerciseSwap,
    },
    forceReadOnly: readOnly,
  });

  const gridTemplateColumns = getGridTemplateColumns(
    displayWorkouts,
    maxNonSetsColumns,
    maxMetricsPerExercise,
    block.status,
    readOnly,
  );

  const onCopy = (data: readonly BlockSelectionData[]) => {
    const groupedData = groupSelectionData(data);
    if (groupedData.length === 0) return;
    const days = groupedData.map((wgd) => {
      const workout = workouts.find((w) => w.workoutId === wgd.workoutId);
      return { assignedDate: workout?.assignedDate ?? null, data: wgd };
    });
    setWorkoutClipboard(buildWorkoutClipboard(days));
  };

  const onPaste = (_: undefined, targetData: readonly BlockSelectionData[]) => {
    const workoutClipboard = jotaiStore.get(workoutClipboardAtom);
    if (!workoutClipboard) return;
    const groupedTargetData = groupSelectionData(targetData);
    if (workoutClipboard.days.length > 1) {
      // Multi-day paste: map clipboard days to target workouts by date offset
      const firstTarget = groupedTargetData[0];
      if (!firstTarget) return;
      const result = collectMultiDayPasteCommandsByPosition(
        workoutClipboard,
        firstTarget.workoutId,
        workouts,
        'BlockDraftWorkoutLayer',
        blockWorkoutLayers,
        setsMetric,
        undefined,
        groupBy,
        firstTarget,
      );
      if (!result.ok) {
        toast({ title: result.error, severity: 'error' });
      } else if (result.value.length > 0) {
        runCommands(result.value);
      }
    } else {
      // Single-day paste: paste directly to the selected cells
      const clipboardData = workoutClipboard.days[0]?.data;
      if (!clipboardData) return;
      const result = pasteCommands(
        'BlockDraftWorkoutLayer',
        blockWorkoutLayers,
        clipboardData,
        groupedTargetData,
        setsMetric,
        undefined,
      );
      if (result.ok) {
        runCommands(result.value);
      } else {
        toast({ title: result.error, severity: 'error' });
      }
    }
  };

  const onBulkClear = (data: readonly BlockSelectionData[]) => {
    const groupedData = groupSelectionData(data);
    const result = commandsForBulkClear(
      'BlockDraftWorkoutLayer',
      blockWorkoutLayers,
      groupedData,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const onDragEnd = (event: DragEndEvent) =>
    handleDragEnd(event, workouts, mutationFns);

  const editContext: SingleWorkoutEditContext = {
    allErrors: workoutPublishErrors,
    mutationFns,
    miscMutationFns,
    applyBenchmarkData,
    hasBlockBeenPublished,
    isPublishDisabled,
    blockStatus: block.status,
    allowFlexWorkouts: block.allowFlexWorkouts,
    getAssignedDate: getAssignedDateWithStub,
    forceReadOnly: readOnly,
    onUndo: undo,
    onRedo: redo,
    onCopy,
    onPaste,
    onBulkClear,
    onDragEnd,
    addWorkout: readOnly
      ? undefined
      : { create: createNewDraftWorkout, cancel: cancelNewDraftWorkout },
    getContextMenuContent: readOnly
      ? null
      : (data, fns) => {
          const groupedData = groupSelectionData(data);
          return getContextMenuContent(
            groupedData,
            getMetric,
            metrics,
            blockWorkoutLayers,
            mutationFns,
            runCommands,
            setInstructionEditData,
            onSetWorkoutTemplate,
            setNewSchemeData,
            t,
            'BlockDraftWorkoutLayer',
            brandColors,
            fns,
            defaultBenchmarkGridId,
            canCreateScheme,
            canCreateTemplate,
            allViewWorkouts,
            athleteDraftWorkoutLayers,
            undefined,
            'all',
            hasBlockBeenPublished,
            onSetMultiWorkoutTemplate,
          );
        },
    onDeleteWorkout: (workoutId: string) => {
      const workout = allViewWorkouts.find((w) => w.workoutId === workoutId);
      if (workout) {
        if (!workout.exists) {
          cancelNewDraftWorkout();
        } else {
          doSessionDelete({
            cycleSortOrder: workout.cycle.id,
            sessionSortOrder: workout.session.id,
          });
        }
      }
    },
    onCancelWorkout: doWorkoutCancel,
  };

  return (
    <>
      {groupBy === 'all' ? (
        <BlockAllView
          block={block}
          workouts={allViewWorkouts}
          applyBenchmarkData={applyBenchmarkData}
          editContext={editContext}
          overlayOpen={!!instructionEditData || !!workoutTemplateData}
        />
      ) : (
        <Sheet
          {...sheetProps}
          sensors={sheetSensors}
          gridTemplateColumns={gridTemplateColumns}
          className="overflow-auto"
          onUndo={undo}
          onRedo={redo}
          onCopy={onCopy}
          onPaste={onPaste}
          onBulkClear={onBulkClear}
          getContextMenuContent={(data) => {
            const groupedData = groupSelectionData(data);
            return getContextMenuContent(
              groupedData,
              getMetric,
              metrics,
              blockWorkoutLayers,
              mutationFns,
              runCommands,
              setInstructionEditData,
              onSetWorkoutTemplate,
              setNewSchemeData,
              t,
              'BlockDraftWorkoutLayer',
              brandColors,
              miscMutationFns,
              defaultBenchmarkGridId,
              canCreateScheme,
              canCreateTemplate,
              workouts,
              athleteDraftWorkoutLayers,
              undefined,
              groupBy,
              hasBlockBeenPublished,
              onSetMultiWorkoutTemplate,
            );
          }}
          readOnly={readOnly}
          onDragEnd={onDragEnd}
          disableKeyboardAndMouse={
            readOnly ||
            !!instructionEditData ||
            !!newSchemeData ||
            modalOrDrawerOpen ||
            !!applySwapData
          }
        />
      )}
      {instructionEditData && (
        <InstructionDrawer
          instruction={instructionEditData.instruction}
          setInstructionEditData={setInstructionEditData}
          editInstruction={(newInstruction) => {
            // if an exercise id is present, this is for an exercise instruction, otheriwse it's for a section
            if (instructionEditData.workoutExerciseId) {
              mutationFns.onEditExercise(
                instructionEditData.workoutId,
                instructionEditData.workoutExerciseId,
                {
                  instruction: newInstruction,
                },
              );
            } else {
              mutationFns.onEditSection(
                instructionEditData.workoutId,
                instructionEditData.workoutSectionId,
                {
                  instruction: newInstruction,
                },
              );
            }
          }}
          maxLength={instructionEditData.workoutExerciseId ? 300 : 1000}
        />
      )}
      {workoutTemplateData && (
        <WorkoutTemplateDrawer
          mode={'create'}
          workoutTemplate={workoutTemplateData}
          tagOptions={workoutTemplateTagOptions}
          onClose={() => setWorkoutTemplateData(null)}
          onSave={onSaveWorkoutTemplate}
          onDelete={() => {
            // no delete on create
          }}
        />
      )}
      {newSchemeData && (
        <NewSchemeDialog
          onCreateScheme={onCreateScheme}
          setsData={newSchemeData}
          focusOptions={focusOptions}
          onClose={() => setNewSchemeData(null)}
        />
      )}
      {pendingMultiApply?.warning === 'overflow' && (
        <ConfirmationModal
          id="apply-multi-workout-template-overflow"
          open
          onConfirm={confirmPendingMultiApply}
          onCancel={() => setPendingMultiApply(null)}
          confirmText={t('continue')}
          {...applyMultiWorkoutTemplateOverflowText(
            t,
            pendingMultiApply.template.name,
            pendingMultiApply.template.template.length,
            pendingMultiApply.plan.dayTargets.length,
          )}
        />
      )}
      {pendingMultiApply?.warning === 'actuals' && (
        <ConfirmationModal
          id="apply-multi-workout-template-actuals"
          open
          onConfirm={confirmPendingMultiApply}
          confirmText={t('ok')}
          {...applyMultiWorkoutTemplateActualsText(
            t,
            pendingMultiApply.plan.actualsDayNumbers,
          )}
        />
      )}
      {pendingMultiApply?.warning === 'draftOverwrite' && (
        <ConfirmationModal
          id="apply-multi-workout-template-draft-overwrite"
          open
          onConfirm={confirmPendingMultiApply}
          onCancel={() => setPendingMultiApply(null)}
          confirmText={t('continue')}
          {...applyMultiWorkoutTemplateDraftOverwriteText(
            t,
            pendingMultiApply.plan.draftWorkoutIds.length,
          )}
        />
      )}
      <DeleteConfirmationModal
        id={`${sessionToDelete?.cycleSortOrder}-${sessionToDelete?.sessionSortOrder}`}
        open={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onDelete={() => {
          setSessionToDelete(null);
          if (sessionToDelete) {
            doSessionDelete(sessionToDelete);
          }
        }}
        {...deleteBlockWorkoutText(t)}
      />

      <DeleteConfirmationModal
        id={workoutToCancel || ''}
        open={!!workoutToCancel}
        onClose={() => setWorkoutToCancel(null)}
        onDelete={() => {
          setWorkoutToCancel(null);
          if (workoutToCancel) {
            doWorkoutCancel(workoutToCancel);
          }
        }}
        title={t('cancelWorkoutForAllAthletes')}
        message={getCancelWorkoutMessage(
          workouts.find((w) => w.workoutId === workoutToCancel),
          t,
        )}
        confirmText={t('yesCancel')}
        cancelText={t('close')}
      />

      <DeleteConfirmationModal
        id={workoutToDelete || ''}
        open={!!workoutToDelete}
        onClose={() => setWorkoutToDelete(null)}
        onDelete={() => {
          setWorkoutToDelete(null);
          if (workoutToDelete) {
            const workout = workouts.find(
              (w) => w.workoutId === workoutToDelete,
            );
            if (workout) {
              doSessionDelete({
                cycleSortOrder: workout.cycle.id,
                sessionSortOrder: workout.session.id,
              });
              toast({
                title: t('success'),
                description: t('workoutDeleted'),
                severity: 'success',
              });
            }
          }
        }}
        {...deleteBlockWorkoutText(t)}
      />

      {applySwapData && (
        <ApplySwapDrawer
          data={applySwapData}
          matchingAthletes={[]}
          onClose={() => setApplySwapData(null)}
          onSave={handleApplySwapSave}
        />
      )}
    </>
  );
}

function getCancelWorkoutMessage(
  workout: WorkoutWithCycleAndSession | undefined,
  t: TFunction<'translation'>,
) {
  if (!workout) {
    return '';
  }
  return t('workoutCanceledForAllAthletesInTrack', {
    workout: getDisplayNameForWorkout(workout),
  });
}
